import React, { useState, useEffect, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { API_BASE_URL } from '../config/api';
import FeatureRequestAttachmentViewer from './FeatureRequestAttachmentViewer';
import {
  getFeatureRequestCommentAttachmentUrl,
  getFeatureRequestDescriptionPreview,
  hasFeatureRequestCommentAttachment,
  resolveFeatureRequestCommentAttachmentUrl
} from '../utils/featureRequestUtils';

const UserFeatureRequestsDialog = ({ showDialog, onClose }) => {
  const { currentUser } = useApp();
  const [userFeatureRequests, setUserFeatureRequests] = useState([]);
  const [editingFeatureRequestId, setEditingFeatureRequestId] = useState(null);
  const [featureRequestMessage, setFeatureRequestMessage] = useState('');
  const [featureRequestMessageType, setFeatureRequestMessageType] = useState('');
  const [showFeatureRequestDetailsDialog, setShowFeatureRequestDetailsDialog] = useState(false);
  const [selectedFeatureRequest, setSelectedFeatureRequest] = useState(null);
  const [featureRequestComments, setFeatureRequestComments] = useState([]);
  const [featureRequestCommentInput, setFeatureRequestCommentInput] = useState('');
  const [featureRequestCommentFile, setFeatureRequestCommentFile] = useState(null);
  const [selectedCommentAttachmentUrl, setSelectedCommentAttachmentUrl] = useState('');
  const featureRequestCommentFileInputRef = useRef(null);
  const [featureRequestCommentMessage, setFeatureRequestCommentMessage] = useState('');
  const [featureRequestCommentMessageType, setFeatureRequestCommentMessageType] = useState('');
  const [loadingFeatureRequestComments, setLoadingFeatureRequestComments] = useState(false);

  useEffect(() => {
    if (showDialog && currentUser?.user?.id) {
      fetchUserFeatureRequests();
    }
  }, [showDialog, currentUser?.user?.id]);

  const fetchUserFeatureRequests = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/feature-request/list/user/${currentUser?.user?.id}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserFeatureRequests(Array.isArray(data) ? data : []);
      } else {
        setUserFeatureRequests([]);
      }
    } catch (error) {
      setUserFeatureRequests([]);
    }
  };

  const saveFeatureRequest = async (featureRequest) => {
    try {
      const payload = {
        title: featureRequest.title,
        description: featureRequest.description,
        priority: featureRequest.priority,
        status: featureRequest.status
      };
      
      if (featureRequest.id && !featureRequest.id.toString().startsWith('temp_')) {
        payload.id = featureRequest.id;
      }
      
      const response = await fetch(`${API_BASE_URL}/feature-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        setFeatureRequestMessage('Feature request saved successfully');
        setFeatureRequestMessageType('success');
        fetchUserFeatureRequests();
        setEditingFeatureRequestId(null);
        setTimeout(() => {
          setFeatureRequestMessage('');
          setFeatureRequestMessageType('');
        }, 3000);
      } else {
        const errorText = await response.text();
        setFeatureRequestMessage(`Error: ${errorText}`);
        setFeatureRequestMessageType('error');
        setTimeout(() => {
          setFeatureRequestMessage('');
          setFeatureRequestMessageType('');
        }, 5000);
      }
    } catch (error) {
      setFeatureRequestMessage(`Error: ${error.message}`);
      setFeatureRequestMessageType('error');
      setTimeout(() => {
        setFeatureRequestMessage('');
        setFeatureRequestMessageType('');
      }, 5000);
    }
  };

  const updateFeatureRequestField = (requestId, field, value) => {
    setUserFeatureRequests(prev => prev.map(request => 
      request.id === requestId ? { ...request, [field]: value } : request
    ));
  };

  const addNewFeatureRequest = () => {
    const tempId = `temp_${Date.now()}`;
    const newRequest = {
      id: tempId,
      title: '',
      description: '',
      priority: 'medium',
      status: 'open'
    };
    setUserFeatureRequests([newRequest, ...userFeatureRequests]);
    setEditingFeatureRequestId(tempId);
  };

  const loadFeatureRequestComments = async (featureRequestId) => {
    if (!featureRequestId) {
      setFeatureRequestComments([]);
      return;
    }

    setLoadingFeatureRequestComments(true);
    try {
      const response = await fetch(`${API_BASE_URL}/feature-request/comment/${featureRequestId}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });

      if (response.ok) {
        const data = await response.json();
        const comments = Array.isArray(data) ? data : data?.comments || data?.data?.comments || data?.data;
        setFeatureRequestComments(Array.isArray(comments) ? comments : []);
      } else {
        setFeatureRequestComments([]);
      }
    } catch (error) {
      setFeatureRequestComments([]);
    } finally {
      setLoadingFeatureRequestComments(false);
    }
  };

  const openFeatureRequestDialog = async (featureRequest) => {
    if (!featureRequest?.id || featureRequest.id.toString().startsWith('temp_')) {
      return;
    }

    setSelectedFeatureRequest({ ...featureRequest });
    setFeatureRequestCommentInput('');
    setFeatureRequestCommentFile(null);
    setFeatureRequestCommentMessage('');
    setFeatureRequestCommentMessageType('');
    setShowFeatureRequestDetailsDialog(true);
    await loadFeatureRequestComments(featureRequest.id);
  };

  const closeFeatureRequestDialog = () => {
    setShowFeatureRequestDetailsDialog(false);
    setSelectedFeatureRequest(null);
    setFeatureRequestComments([]);
    setFeatureRequestCommentInput('');
    setFeatureRequestCommentFile(null);
    setFeatureRequestCommentMessage('');
    setFeatureRequestCommentMessageType('');
  };

  const parseUtcCommentDatetime = (value) => {
    if (!value) return null;
    const rawValue = String(value).trim();
    const hasTimezone = /z$/i.test(rawValue) || /[+-]\d{2}:\d{2}$/.test(rawValue);
    const normalizedValue = hasTimezone ? rawValue : `${rawValue}Z`;
    const parsedDate = new Date(normalizedValue);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  };

  const formatCommentDatetimeLocal = (value) => {
    const parsedDate = parseUtcCommentDatetime(value);
    return parsedDate ? parsedDate.toLocaleString() : '';
  };

  const saveFeatureRequestComment = async () => {
    const featureRequestId = selectedFeatureRequest?.id;
    const comment = featureRequestCommentInput.trim();

    if (!featureRequestId || !comment) {
      setFeatureRequestCommentMessage('Comment cannot be empty');
      setFeatureRequestCommentMessageType('error');
      return;
    }

    try {
      const payload = {
        featureRequestId,
        commentDatetime: new Date().toISOString(),
        userId: currentUser?.user?.id || currentUser?.id,
        comment
      };
      let body = JSON.stringify(payload);
      const headers = {
        'Content-Type': 'application/json',
        'session-id': currentUser?.sessionId
      };

      if (featureRequestCommentFile) {
        const formData = new FormData();
        Object.entries(payload).forEach(([key, value]) => formData.append(key, value));
        formData.append('attachment', featureRequestCommentFile);
        body = formData;
        delete headers['Content-Type'];
      }

      const response = await fetch(`${API_BASE_URL}/feature-request/comment`, {
        method: 'POST',
        headers,
        body
      });

      if (response.ok) {
        setFeatureRequestCommentInput('');
        setFeatureRequestCommentFile(null);
        if (featureRequestCommentFileInputRef.current) featureRequestCommentFileInputRef.current.value = '';
        setFeatureRequestCommentMessage('Comment saved');
        setFeatureRequestCommentMessageType('success');
        await loadFeatureRequestComments(featureRequestId);
        setTimeout(() => {
          setFeatureRequestCommentMessage('');
          setFeatureRequestCommentMessageType('');
        }, 5000);
      } else {
        const errorText = await response.text();
        setFeatureRequestCommentMessage(`Error: ${errorText}`);
        setFeatureRequestCommentMessageType('error');
      }
    } catch (error) {
      setFeatureRequestCommentMessage(`Error: ${error.message}`);
      setFeatureRequestCommentMessageType('error');
    }
  };

  const handleFeatureRequestCommentFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024) {
      setFeatureRequestCommentMessage('Attachment must be 1 MB or smaller');
      setFeatureRequestCommentMessageType('error');
      event.target.value = '';
      return;
    }
    setFeatureRequestCommentFile(file);
    setFeatureRequestCommentMessage('');
    setFeatureRequestCommentMessageType('');
  };

  const updateSelectedFeatureRequestField = (field, value) => {
    setSelectedFeatureRequest((prev) => prev ? { ...prev, [field]: value } : prev);
  };

  const saveSelectedFeatureRequest = async () => {
    if (!selectedFeatureRequest?.id) {
      return;
    }

    await saveFeatureRequest(selectedFeatureRequest);
    setSelectedFeatureRequest((prev) => prev ? { ...prev } : prev);
  };

  if (!showDialog) return null;

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
      <div style={{ backgroundColor: 'white', padding: '0', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', width: '800px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ position: 'relative', backgroundColor: '#e6f7ff', borderBottom: '1px solid #ccc', borderTopLeftRadius: '8px', borderTopRightRadius: '8px', padding: '1rem' }}>
          <h3 style={{ textAlign: 'center', margin: '0' }}>My Feature Requests</h3>
          <button onClick={onClose} style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' }}>×</button>
        </div>
        
        <div style={{ padding: '1rem', borderBottom: '1px solid #ccc' }}>
          {featureRequestMessage && (
            <div style={{ 
              padding: '0.75rem', 
              marginBottom: '1rem', 
              borderRadius: '4px', 
              backgroundColor: featureRequestMessageType === 'success' ? '#d4edda' : '#f8d7da',
              color: featureRequestMessageType === 'success' ? '#155724' : '#721c24',
              border: `1px solid ${featureRequestMessageType === 'success' ? '#c3e6cb' : '#f5c6cb'}`
            }}>
              {featureRequestMessage}
            </div>
          )}
          <button 
            onClick={addNewFeatureRequest}
            style={{ 
              padding: '0.5rem 1rem', 
              backgroundColor: '#28a745', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px', 
              cursor: 'pointer' 
            }}
          >
            Add New Request
          </button>
        </div>
        
        <div style={{ flex: 1, overflow: 'auto', padding: '1rem' }}>
          {userFeatureRequests.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#6c757d', padding: '2rem' }}>No feature requests yet</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Actions</th>
                    <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Title</th>
                    <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Description</th>
                    <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Priority</th>
                    <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Status</th>
                    <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {userFeatureRequests.map((request) => {
                    const isEditing = editingFeatureRequestId === request.id;
                    return (
                      <tr key={request.id}>
                        <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                          <div style={{ display: 'flex', gap: '0.25rem' }}>
                            {isEditing ? (
                              <>
                                <button onClick={() => saveFeatureRequest(request)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>💾</button>
                                <button onClick={() => {
                                  if (request.id && request.id.toString().startsWith('temp_')) {
                                    setUserFeatureRequests(prev => prev.filter(r => r.id !== request.id));
                                  }
                                  setEditingFeatureRequestId(null);
                                }} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>❌</button>
                              </>
                            ) : (
                              <button onClick={() => setEditingFeatureRequestId(request.id)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>✏️</button>
                            )}
                          </div>
                        </td>
                        <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                          {isEditing ? 
                            <input type="text" value={request.title || ''} onChange={(e) => updateFeatureRequestField(request.id, 'title', e.target.value)} style={{ width: '100%', border: 'none' }} /> : 
                            (
                              <span
                                onClick={() => openFeatureRequestDialog(request)}
                                style={{ cursor: 'pointer', color: '#007bff', textDecoration: 'underline' }}
                                title="Open feature request details"
                              >
                                {request.title || ''}
                              </span>
                            )
                          }
                        </td>
                        <td style={{ border: '1px solid #dee2e6', padding: '0.75rem', maxWidth: '300px', wordWrap: 'break-word' }}>
                          {isEditing ? 
                            <textarea value={request.description || ''} onChange={(e) => updateFeatureRequestField(request.id, 'description', e.target.value)} style={{ width: '100%', border: 'none', resize: 'vertical', minHeight: '60px' }} /> : 
                            getFeatureRequestDescriptionPreview(request.description)
                          }
                        </td>
                        <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                          {isEditing ? (
                            <select value={request.priority || 'medium'} onChange={(e) => updateFeatureRequestField(request.id, 'priority', e.target.value)} style={{ width: '100%', border: 'none' }}>
                              <option value="low">Low</option>
                              <option value="medium">Medium</option>
                              <option value="high">High</option>
                              <option value="highest">Highest</option>
                              <option value="critical">Critical</option>
                            </select>
                          ) : (
                            <span style={{ 
                              padding: '0.25rem 0.5rem', 
                              borderRadius: '4px', 
                              fontSize: '0.8rem',
                              backgroundColor: request.priority === 'critical' ? '#450a0a' : request.priority === 'highest' ? '#721c24' : request.priority === 'high' ? '#f8d7da' : request.priority === 'medium' ? '#fff3cd' : '#d4edda',
                              color: ['critical', 'highest'].includes(request.priority) ? 'white' : request.priority === 'high' ? '#721c24' : request.priority === 'medium' ? '#856404' : '#155724'
                            }}>
                              {request.priority ? request.priority.charAt(0).toUpperCase() + request.priority.slice(1) : 'Medium'}
                            </span>
                          )}
                        </td>
                        <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                          <span style={{ 
                            padding: '0.25rem 0.5rem', 
                            borderRadius: '4px', 
                            fontSize: '0.8rem',
                            backgroundColor: request.status === 'completed' ? '#d4edda' : request.status === 'in_progress' ? '#cce5ff' : '#f8d7da',
                            color: request.status === 'completed' ? '#155724' : request.status === 'in_progress' ? '#004085' : '#721c24'
                          }}>
                            {request.status ? request.status.replace('_', ' ') : 'open'}
                          </span>
                        </td>
                        <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                          {request.createdAt ? new Date(request.createdAt).toLocaleDateString() : ''}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        
        <div style={{ textAlign: 'center', padding: '1rem', borderTop: '1px solid #ccc', backgroundColor: '#f8f9fa', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px' }}>
          <button onClick={onClose} style={{ padding: '0.5rem 1rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Close</button>
        </div>
      </div>

      {showFeatureRequestDetailsDialog && selectedFeatureRequest && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2100 }}>
          <div style={{ backgroundColor: 'white', width: '900px', maxWidth: '95vw', maxHeight: '90vh', borderRadius: '8px', boxShadow: '0 6px 24px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ position: 'relative', backgroundColor: '#e6f7ff', borderBottom: '1px solid #ccc', borderTopLeftRadius: '8px', borderTopRightRadius: '8px', padding: '1rem 1.25rem' }}>
              <h3 style={{ margin: 0, textAlign: 'center' }}>Feature Request</h3>
              <button onClick={closeFeatureRequestDialog} style={{ position: 'absolute', top: '0.5rem', right: '0.6rem', background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#666' }}>×</button>
            </div>
            <div style={{ padding: '1rem 1.25rem', overflow: 'auto' }}>
              <div style={{ marginBottom: '0.5rem' }}><strong>Title:</strong> {selectedFeatureRequest.title || ''}</div>
              <div style={{ marginBottom: '0.5rem' }}><strong>Description:</strong> {selectedFeatureRequest.description || ''}</div>
              {currentUser?.user?.orgRole === 'superuser' ? (
                <>
                  <div style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <strong style={{ minWidth: '70px' }}>Priority:</strong>
                    <select value={selectedFeatureRequest.priority || 'medium'} onChange={(e) => updateSelectedFeatureRequestField('priority', e.target.value)} style={{ padding: '0.35rem 0.5rem', border: '1px solid #ccc', borderRadius: '4px', minWidth: '140px' }}>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="highest">Highest</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                  <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <strong style={{ minWidth: '70px' }}>Status:</strong>
                    <select value={selectedFeatureRequest.status || 'open'} onChange={(e) => updateSelectedFeatureRequestField('status', e.target.value)} style={{ padding: '0.35rem 0.5rem', border: '1px solid #ccc', borderRadius: '4px', minWidth: '170px' }}>
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="rejected">Rejected</option>
                      <option value="duplicate">Duplicate</option>
                    </select>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                    <button onClick={saveSelectedFeatureRequest} style={{ padding: '0.45rem 0.8rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Save Changes</button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ marginBottom: '0.5rem' }}><strong>Priority:</strong> {selectedFeatureRequest.priority || ''}</div>
                  <div style={{ marginBottom: '1rem' }}><strong>Status:</strong> {selectedFeatureRequest.status || ''}</div>
                </>
              )}

              <div style={{ borderTop: '1px solid #dee2e6', paddingTop: '1rem' }}>
                <h4 style={{ marginTop: 0, marginBottom: '0.75rem' }}>Comments</h4>
                {featureRequestCommentMessage && (
                  <div style={{ padding: '0.6rem', marginBottom: '0.75rem', borderRadius: '4px', backgroundColor: featureRequestCommentMessageType === 'success' ? '#d4edda' : '#f8d7da', color: featureRequestCommentMessageType === 'success' ? '#155724' : '#721c24', border: `1px solid ${featureRequestCommentMessageType === 'success' ? '#c3e6cb' : '#f5c6cb'}` }}>
                    {featureRequestCommentMessage}
                  </div>
                )}

                <div style={{ marginBottom: '0.75rem' }}>
                  <textarea
                    value={featureRequestCommentInput}
                    onChange={(e) => setFeatureRequestCommentInput(e.target.value)}
                    placeholder="Add a comment..."
                    rows="3"
                    style={{ width: '100%', padding: '0.65rem', border: '1px solid #ccc', borderRadius: '4px', resize: 'vertical' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                    <div>
                      <input ref={featureRequestCommentFileInputRef} type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={handleFeatureRequestCommentFileChange} />
                      <span style={{ marginLeft: '0.5rem', color: '#6c757d', fontSize: '0.8rem' }}>PDF/JPEG/PNG, max 1 MB</span>
                      {featureRequestCommentFile && <div style={{ color: '#6c757d', fontSize: '0.8rem' }}>Selected: {featureRequestCommentFile.name}</div>}
                    </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                    <button onClick={saveFeatureRequestComment} style={{ padding: '0.45rem 0.8rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Save Comment</button>
                  </div>
                  </div>
                </div>

                <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px', backgroundColor: '#f8f9fa' }}>
                  {loadingFeatureRequestComments ? (
                    <div style={{ padding: '0.9rem', color: '#6c757d' }}>Loading comments...</div>
                  ) : featureRequestComments.length === 0 ? (
                    <div style={{ padding: '0.9rem', color: '#6c757d' }}>No comments yet</div>
                  ) : (
                    featureRequestComments
                      .slice()
                      .sort((a, b) => {
                        const aTime = parseUtcCommentDatetime(a.commentDatetime)?.getTime() || 0;
                        const bTime = parseUtcCommentDatetime(b.commentDatetime)?.getTime() || 0;
                        return aTime - bTime;
                      })
                      .map((commentItem) => (
                        <div key={commentItem.id} style={{ padding: '0.75rem', borderBottom: '1px solid #e5e5e5', backgroundColor: 'white' }}>
                          <div style={{ fontSize: '0.82rem', color: '#6c757d', marginBottom: '0.3rem' }}>
                            {commentItem.userName || `User ${commentItem.userId || ''}`} • {formatCommentDatetimeLocal(commentItem.commentDatetime)}
                          </div>
                          <div style={{ whiteSpace: 'pre-wrap' }}>{commentItem.comment || ''}</div>
                          {hasFeatureRequestCommentAttachment(commentItem) && (() => {
                            const attachmentUrl = getFeatureRequestCommentAttachmentUrl(commentItem);
                            return attachmentUrl ? (
                              <button type="button" onClick={() => setSelectedCommentAttachmentUrl(resolveFeatureRequestCommentAttachmentUrl(attachmentUrl, API_BASE_URL))} style={{ display: 'inline-block', marginTop: '0.4rem', padding: 0, border: 'none', background: 'none', color: '#007bff', cursor: 'pointer' }}>
                                📎 View attachment
                              </button>
                            ) : (
                              <span title="Attachment available" style={{ display: 'inline-block', marginTop: '0.4rem' }}>📎 Attachment</span>
                            );
                          })()}
                        </div>
                      ))
                  )}
                </div>
              </div>
            </div>
            <div style={{ textAlign: 'center', padding: '0.9rem', borderTop: '1px solid #ccc', backgroundColor: '#f8f9fa', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px' }}>
              <button onClick={closeFeatureRequestDialog} style={{ padding: '0.5rem 1rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}
      <FeatureRequestAttachmentViewer url={selectedCommentAttachmentUrl} onClose={() => setSelectedCommentAttachmentUrl('')} />
    </div>
  );
};

export default UserFeatureRequestsDialog;